import React from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { colorFor } from '../../../utils/itemPalette';
import { fpColors } from '../../../styles/theme';
import type { Bbox, ImageTransform } from '../useImageTransform';

/**
 * Per-focus action strip anchored to the currently-focused bbox.
 *
 * Replaces the old always-visible per-bbox chip. With 20+ items
 * streamed from a crowded scan, a chip per bbox quickly clutters
 * the image (especially when bboxes overlap in frame space). The
 * focus model — one bbox focused at a time, with contextual
 * actions anchored to it — is what Apple Photos / Pinterest Lens
 * / Google Photos use.
 *
 * Strip contents:
 *   - ✓ / ○  toggle the accepted set
 *   - ✎       enter refine mode for this item
 *   - 🗑      delete (with feedback row server-side)
 *
 * Positioning: below the bbox by default. If there's not enough
 * room between the bbox bottom and the image's render-rect
 * bottom, flips above. Horizontally clamped to stay within the
 * image bounds.
 */

export interface ControlsLayerItem {
  id: number;
  bbox: Bbox;
  selected: boolean;
  lowConfidence?: boolean;
}

interface ControlsLayerProps {
  transform: ImageTransform;
  items: ControlsLayerItem[];
  onToggleSelected: (id: number) => void;
  onRefine?: (id: number) => void;
  onDelete?: (id: number) => void;
  /** The id of the focused bbox. Only this item gets the strip. */
  focusedItemId?: number | null;
  /** Pass the id currently being edited to suppress the strip
   *  (RefineOverlay owns the frame during refine). */
  editingItemId?: number | null;
}

const STRIP_HEIGHT = 40;
const STRIP_GAP = 8;
const MIN_STRIP_WIDTH = 130;

export const ControlsLayer: React.FC<ControlsLayerProps> = ({
  transform,
  items,
  onToggleSelected,
  onRefine,
  onDelete,
  focusedItemId,
  editingItemId,
}) => {
  if (!transform.ready) return null;
  if (focusedItemId === null || focusedItemId === undefined) return null;
  if (editingItemId === focusedItemId) return null;
  // #170 fix — read-only hosts (saved scene) pass no handlers; without
  // this the white action-strip container still rendered as an empty
  // pill following the focused bbox. No actions → no strip.
  if (!onRefine && !onDelete) return null;

  const item = items.find((it) => it.id === focusedItemId);
  if (!item) return null;

  const rect = transform.toDomBbox(item.bbox);
  const palette = colorFor(item.id);
  const renderRect = transform.renderRect;

  // Preferred position: directly below the bbox, anchored to its
  // left edge. Flip above if the strip would clip the bottom of
  // the image render rect.
  const stripBottomNeeded = rect.y + rect.h + STRIP_GAP + STRIP_HEIGHT;
  const bottomLimit = renderRect
    ? renderRect.y + renderRect.h - 2
    : Infinity;
  const placeAbove = stripBottomNeeded > bottomLimit;
  const stripTop = placeAbove
    ? Math.max(2, rect.y - STRIP_GAP - STRIP_HEIGHT)
    : rect.y + rect.h + STRIP_GAP;

  // Horizontal clamp to stay inside the image render rect.
  const leftLimit = renderRect ? renderRect.x + 2 : 2;
  const rightLimit = renderRect
    ? renderRect.x + renderRect.w - 2
    : Infinity;
  const desiredLeft = rect.x;
  const stripWidth = MIN_STRIP_WIDTH;
  const stripLeft = Math.max(
    leftLimit,
    Math.min(desiredLeft, rightLimit - stripWidth),
  );

  const onPressSelect = () => onToggleSelected(item.id);
  const onPressRefine = () => onRefine?.(item.id);
  const onPressDelete = () => onDelete?.(item.id);

  return (
    <View pointerEvents="box-none" style={StyleSheet.absoluteFill}>
      <View
        style={[
          styles.strip,
          {
            left: stripLeft,
            top: stripTop,
            minWidth: stripWidth,
          },
        ]}
      >
        {/* Select / Deselect — primary action, color-coded to the
            item's palette when selected. */}
        <Pressable
          onPress={onPressSelect}
          hitSlop={6}
          accessibilityRole="button"
          accessibilityLabel={item.selected ? 'Deselect item' : 'Select item'}
          accessibilityState={{ selected: item.selected }}
          style={({ pressed }) => [
            styles.btn,
            {
              backgroundColor: item.selected
                ? palette.solid
                : 'rgba(255,255,255,0.96)',
              borderColor: palette.solid,
              borderWidth: item.selected ? 0 : 2,
            },
            pressed && { opacity: 0.85 },
          ]}
        >
          {item.selected ? (
            <MaterialIcons name="check" size={18} color={palette.onSolid} />
          ) : null}
        </Pressable>

        {/* Refine — same `crop-free` glyph the rail card's
            "Refine box" chip uses, for visual consistency. */}
        {onRefine ? (
          <Pressable
            onPress={onPressRefine}
            hitSlop={6}
            accessibilityRole="button"
            accessibilityLabel="Refine bounding box"
            style={({ pressed }) => [
              styles.btn,
              styles.btnNeutral,
              pressed && { opacity: 0.85 },
            ]}
          >
            <MaterialIcons
              name="crop-free"
              size={16}
              color={fpColors.onSurface}
            />
          </Pressable>
        ) : null}

        {/* Delete — writes annotation_feedback server-side before
            soft-delete, so the rejection isn't lost. */}
        {onDelete ? (
          <Pressable
            onPress={onPressDelete}
            hitSlop={6}
            accessibilityRole="button"
            accessibilityLabel="Delete item"
            style={({ pressed }) => [
              styles.btn,
              styles.btnDanger,
              pressed && { opacity: 0.85 },
            ]}
          >
            <MaterialIcons
              name="delete-outline"
              size={16}
              color={fpColors.error}
            />
          </Pressable>
        ) : null}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  strip: {
    position: 'absolute',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    padding: 4,
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.98)',
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    ...(Platform.OS === 'web'
      ? ({
          boxShadow: '0 6px 18px rgba(0,0,0,0.25)',
        } as any)
      : null),
  },
  btn: {
    width: 30,
    height: 30,
    borderRadius: 15,
    justifyContent: 'center',
    alignItems: 'center',
  },
  btnNeutral: {
    backgroundColor: fpColors.surfaceContainerLow,
  },
  btnDanger: {
    backgroundColor: fpColors.errorContainer,
  },
});
